import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Wrench } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../../lib/api";
import UploadButton from "./UploadButton";

const empty = { name: "", type: "spillatore", description: "", cost: 0, image_url: "", monthly_target: 0, active: true };

export default function MachinesPanel() {
  const [machines, setMachines] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/machines").then(({ data }) => setMachines(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openForm = (m) => { setForm(m ? { ...m } : empty); setEditId(m?.id || null); setOpen(true); };
  const save = async () => {
    if (!form.name) { toast.error("Inserisci il nome della macchina"); return; }
    try {
      const payload = { name: form.name, type: form.type, description: form.description, cost: Number(form.cost), image_url: form.image_url, monthly_target: Number(form.monthly_target) || 0, active: form.active };
      if (editId) await api.put(`/machines/${editId}`, payload);
      else await api.post("/machines", payload);
      toast.success("Macchina salvata"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/machines/${id}`); toast.success("Eliminata"); load(); };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openForm(null)} data-testid="add-machine-btn" className="bg-[#0F4C81] text-white rounded-full"><Plus className="w-4 h-4 mr-2" />Nuova macchina</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Modifica" : "Nuova"} macchina</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input data-testid="machine-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1" data-testid="machine-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spillatore">Spillatore</SelectItem>
                      <SelectItem value="granitore">Granitore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Costo impianto €</Label><Input type="number" step="0.01" data-testid="machine-cost-input" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Descrizione</Label><Textarea data-testid="machine-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
              <div><Label>Target mensile (fusti/consumi per il comodato)</Label><Input type="number" data-testid="machine-target-input" value={form.monthly_target} onChange={(e) => setForm({ ...form, monthly_target: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Foto macchina</Label>
                <div className="flex gap-2 mt-1 items-center">
                  {form.image_url ? <img src={form.image_url} alt="" className="w-12 h-12 rounded-lg object-cover border" /> : null}
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL o carica" className="flex-1" data-testid="machine-image-input" />
                  <UploadButton onUploaded={(url) => setForm({ ...form, image_url: url })} testid="machine-image-upload" />
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-machine-btn" className="bg-[#0F4C81] text-white rounded-full">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {machines.length === 0 && <p className="text-stone-400">Nessuna macchina. Aggiungine una.</p>}
        {machines.map((m) => (
          <div key={m.id} className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid={`machine-card-${m.id}`}>
            <div className="aspect-[4/3] bg-stone-100">
              {m.image_url ? <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone-300"><Wrench className="w-10 h-10" /></div>}
            </div>
            <div className="p-4">
              <div className="text-[0.68rem] uppercase tracking-[0.18em] font-semibold text-[#C2593F] mb-1">{m.type}</div>
              <h3 className="font-serif text-lg font-semibold text-[#0B2A45]">{m.name}</h3>
              <p className="text-sm text-stone-500 mt-1 mb-3 min-h-[40px]">{m.description}</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Costo <b className="font-mono text-[#0F4C81]">{eur(m.cost)}</b></span>
                <span className="text-stone-500">Target <b className="font-mono text-[#15803D]">{m.monthly_target ?? 0}/mese</b></span>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openForm(m)} data-testid={`edit-machine-${m.id}`}><Pencil className="w-4 h-4 text-stone-500 hover:text-[#0F4C81]" /></button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-machine-${m.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="delete-machine-confirm">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-serif">Eliminare la macchina?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Stai per eliminare «{m.name}». L'operazione non può essere annullata.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-machine-cancel">Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(m.id)} data-testid={`confirm-delete-machine-${m.id}`} className="bg-red-600 hover:bg-red-700 text-white">Elimina</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
